// Analytics Query Types
export enum QueryPeriods {
  day = 'day',
  week = 'week',
  month = 'month',
}

// Analytics Summary
export interface AnalyticsSummaryDto {
  todaysRake: number;
  rolling7DaysRake: number;
  currentCycleRake: number;
  currentCycleStart: string;
  currentCycleEnd: string;
  tablesRunning: number;
  usdtInPlay: number;
  activePlayers: number;
}

export interface AnalyticsQueryDto {
  fromDate?: string;
  toDate?: string;
  period?: QueryPeriods;
  groupByDate?: boolean;
  userId?: string;
  gameType?: string;
  referralSource?: string;
}

// Player Metrics
export interface PlayerMetricsDto {
  dau: number;
  wau: number;
  mau: number;
  dauChange: number;
  wauChange: number;
  mauChange: number;
  newRegistrations: number;
  kycVerified: number;
  firstDepositConversions: number;
  churnRate: number;
  playerSegments: {
    casual: number;
    regular: number;
    whales: number;
  };
  geoDistribution: Array<{
    country: string;
    count: number;
    percentage: number;
  }>;
}

// Financial Metrics
export interface FinancialMetricsDto {
  totalRakeGenerated: number;
  rakeSharePaidUsdt: number;
  rakebackPaidPhnm: number;
  netRake: number;
  revenueShareAmount: number;
  tokenRedemptionCosts: number;
  netRevenue: number;
  arpu: number;
  arpdau: number;
  ltvByCohort: Array<{
    cohort: string;
    ltv: number;
  }>;
  weeklyRakeShareMetrics: {
    totalInUSDT: number;
    totalPendingUsdt: number;
    totalPendingPhnm: number;
    phnmPayoutPercentage: number;
  };
  grossDeposits: number;
  totalWithdrawals: number;
  depositWithdrawalRatio: number;
  depositsByChannel: Array<{
    channel: string;
    amount: number;
    count: number;
  }>;
  bonusCosts: number;
}

// Game Health Metrics
export interface GameHealthMetricsDto {
  tablesRunning: number;
  tablesByType: Array<{
    gameType: string;
    count: number;
    stakes: string;
  }>;
  avgWaitingTime: number;
  handsPlayed: number;
  avgPotSize: Array<{
    gameType: string;
    avgPot: number;
  }>;
  winLossDistribution: {
    winners: number;
    losers: number;
    breakeven: number;
  };
  rakeByHour: Array<{
    hour: number;
    rake: number;
  }>;
  tableAbandonmentRate: number;
  liquidityDropOffHours: number[];
  tournamentMetrics: {
    registrations: number;
    lateRegPercentage: number;
    overlayCosts: number;
  };
}

// Retention Cohort
export interface RetentionCohortDto {
  cohort: string;
  users: number;
  d1Retention: number;
  d7Retention: number;
  d30Retention: number;
  d60Retention?: number;
  d90Retention?: number;
}

// Funnel Metrics
export interface FunnelMetricsDto {
  signups: number;
  emailVerified: number;
  kycStarted: number;
  kycCompleted: number;
  firstDeposit: number;
  firstHandPlayed: number;
  repeatPlay: number;
  conversionRates: {
    signupToVerified: number;
    verifiedToKycStarted: number;
    kycStartedToCompleted: number;
    kycToDeposit: number;
    depositToPlay: number;
    playToRepeat: number;
  };
}

// Player Behavior Metrics
export interface PlayerBehaviorMetricsDto {
  depositFrequency: {
    daily: number;
    weekly: number;
    monthly: number;
  };
  avgTicketSize: number;
  crossGameEngagement: {
    cashOnly: number;
    tournamentOnly: number;
    mixed: number;
  };
  riskFlags: {
    multiAccounting: any[];
    collusion: any[];
    suspiciousPatterns: any[];
  };
  behaviorIndicators: {
    tiltingPlayers: number;
    chasingLosses: number;
  };
}

// Marketing ROI Metrics
export interface MarketingROIMetricsDto {
  cpaByChannel: Array<{
    channel: string;
    cpa: number;
    conversions: number;
  }>;
  affiliateROI: {
    totalCost: number;
    totalRevenue: number;
    roi: number;
  };
  promotionImpact: {
    totalCost: number;
    revenueGenerated: number;
    playersActivated: number;
  };
  referralMetrics: {
    kFactor: number;
    viralCoefficient: number;
    referralRate: number;
  };
  inactiveAmbassadorPlayers: number;
}
